import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
} from '@mui/material';
import QuestionFormModal from './QuestionFormModal';
import DetectedKeysTable from './DetectedKeysTable';
import { getValidKeys, cleanupOrphanQuestions, generateDisplayTextFromKey } from '../../utils/templateKeyUtils';

/**
 * AnswerContentEditor - Component for editing answer branch content
 * 
 * Features:
 * - Paragraph template editor (with {{key}} support)
 * - Nested questions management (add, edit, delete)
 * - Integration with QuestionFormModal for nested questions
 * - Recursive question structure support
 * 
 * Phase 6A: T4.6 - Answer Content Editor
 */
const AnswerContentEditor = ({ open, answer, allQuestions = [], parentQuestionId = null, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    paragraph: '',
    nestedQuestions: [],
  });

  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [editingNestedQuestion, setEditingNestedQuestion] = useState(null);
  const [parentContext, setParentContext] = useState(null); // { parentId, answerValue }

  // Get valid keys from paragraph
  const paragraphValidKeys = useMemo(() => {
    if (!formData.paragraph || typeof formData.paragraph !== 'string') {
      return [];
    }
    return getValidKeys(formData.paragraph);
  }, [formData.paragraph]);

  // Orphan cleanup for nested questions when paragraph changes
  useEffect(() => {
    const validKeys = getValidKeys(formData.paragraph || '');
    const cleanedNested = cleanupOrphanQuestions(
      formData.nestedQuestions || [],
      validKeys
    );
    
    if (cleanedNested.length !== (formData.nestedQuestions?.length || 0)) {
      setFormData((prev) => ({
        ...prev,
        nestedQuestions: cleanedNested
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.paragraph]);

  // Initialize form data when answer changes
  useEffect(() => {
    if (answer) {
      setFormData({
        paragraph: answer.content?.paragraph || '',
        nestedQuestions: answer.content?.nestedQuestions || [],
      });
    } else {
      setFormData({
        paragraph: '',
        nestedQuestions: [],
      });
    }
  }, [answer]);

  // Generate temporary ID for new questions
  const generateTempId = () => {
    return `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  };

  // Handle add nested question (from DetectedKeysTable)
  const handleAddNestedQuestion = (key) => {
    const newNestedQuestion = {
      _tempId: `nested-temp-${Date.now()}`,
      key: key,
      displayText: generateDisplayTextFromKey(key),
      type: 'Text',
      required: true,
      repeatable: false,
      _isNested: true, // Flag for QuestionFormModal (key readonly)
    };
    setEditingNestedQuestion(newNestedQuestion);
    // parentId: parent question'ın _id/_tempId'si
    // parentQuestionId prop'u QuestionFormModal'dan geliyor (formData._id || formData._tempId)
    // answer.parentId fallback olarak kullanılabilir (recursive nested questions için)
    setParentContext({
      parentId: parentQuestionId || answer?.parentId || null,
      answerValue: answer?.value || null,
    });
    setShowQuestionModal(true);
  };

  // Handle edit nested question (from DetectedKeysTable)
  const handleEditNestedQuestion = (question) => {
    // Ensure _isNested flag is set for QuestionFormModal
    // Preserve existing parentId (for recursive nested questions)
 const questionWithFlag = {
      ...question, 
      _isNested: true,
      // parentId should already be set from previous save, but ensure it's set
      parentId: question.parentId || parentQuestionId || answer?.parentId || null,
    };
    
    setEditingNestedQuestion(questionWithFlag);
    // Set parentContext for recursive nested questions (if question has answers with nested questions)
    setParentContext({
      parentId: question.parentId || parentQuestionId || answer?.parentId || null,
      answerValue: answer?.value || null,
    });
    setShowQuestionModal(true);
  };

  // Handle save nested question (key-based matching)
  const handleSaveNestedQuestion = (savedQuestion) => {
    // Find existing question by key (key-based matching)
    const existingIndex = formData.nestedQuestions.findIndex(
      q => q.key === savedQuestion.key
    );

    // Determine parentId: parentQuestionId prop > parentContext > answer.parentId
    // parentQuestionId: Direct parent question (for first level nested questions)
    // answer.parentId: Parent question from answer branch (for recursive nested questions)
    const finalParentId = parentQuestionId || parentContext?.parentId || answer?.parentId || null;
    const finalParentAnswerValue = parentContext?.answerValue || answer?.value || null;

    if (existingIndex >= 0) {
      // Update existing question
      const updated = formData.nestedQuestions.map((q, index) =>
        index === existingIndex
          ? {
              ...savedQuestion,
              _tempId: q._tempId, // Preserve temp ID
              _id: q._id, // Preserve database ID
              _isNested: true, // Preserve _isNested flag
              parentId: q.parentId || finalParentId, // Preserve existing parentId or use finalParentId
              parentAnswerValue: q.parentAnswerValue || finalParentAnswerValue, // Preserve existing parentAnswerValue or use finalParentAnswerValue
            }
          : q
      );
      setFormData((prev) => ({ ...prev, nestedQuestions: updated }));
    } else {
      // Add new question
      const newQuestion = {
        ...savedQuestion,
        _tempId: savedQuestion._tempId || generateTempId(),
        _isNested: true, // Ensure _isNested flag is set
        parentId: finalParentId, // Use determined parentId
        parentAnswerValue: finalParentAnswerValue, // Use determined parentAnswerValue
        order: formData.nestedQuestions.length,
      };
      setFormData((prev) => ({
        ...prev,
        nestedQuestions: [...prev.nestedQuestions, newQuestion],
      }));
    }
    setShowQuestionModal(false);
    setEditingNestedQuestion(null);
    setParentContext(null);
  };

  // Handle paragraph change
  const handleParagraphChange = (paragraph) => {
    setFormData((prev) => ({ ...prev, paragraph }));
  };

  // Handle save
  const handleSave = () => {
    const answerContent = {
      paragraph: formData.paragraph,
      nestedQuestions: formData.nestedQuestions,
    };
    onSave({
      ...answer,
      content: answerContent,
    });
    onClose();
  };

  // Handle close
  const handleClose = () => {
    setShowQuestionModal(false);
    setEditingNestedQuestion(null);
    setParentContext(null);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          Cevap İçeriği Düzenle
        </DialogTitle>

        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            {/* Paragraph Template */}
            <Box>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Paragraf Şablonu
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={formData.paragraph}
                onChange={(e) => handleParagraphChange(e.target.value)}
                placeholder="Cevap seçildiğinde gösterilecek metin ({{key}} kullanabilirsiniz)"
                helperText="Paragraf şablonu, cevap seçildiğinde gösterilecek metni belirler. {{key}} ile soru cevaplarını ekleyebilirsiniz."
              />
            </Box>

            {/* Nested Questions */}
            <Box>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Alt Sorular (Paragrafta kullanılan key'ler)
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                Paragrafta kullanılan key'ler için alt sorular tanımlayabilirsiniz. Bu, cevaba göre dinamik soru oluşturmanıza olanak sağlar.
              </Alert>

              <DetectedKeysTable
                validKeys={paragraphValidKeys}
                questions={formData.nestedQuestions || []}
                onAdd={(key) => handleAddNestedQuestion(key)}
                onEdit={(question) => handleEditNestedQuestion(question)}
              />
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>İptal</Button>
          <Button onClick={handleSave} variant="contained">
            Kaydet
          </Button>
        </DialogActions>
      </Dialog>

      {/* Question Form Modal for nested questions */}
      {showQuestionModal && (
        <QuestionFormModal
          open={showQuestionModal}
          question={editingNestedQuestion}
          parentContext={parentContext}
          allQuestions={allQuestions}
          onClose={() => {
            setShowQuestionModal(false);
            setEditingNestedQuestion(null);
            setParentContext(null);
          }}
          onSave={handleSaveNestedQuestion}
        />
      )}
    </>
  );
};

AnswerContentEditor.propTypes = {
  open: PropTypes.bool.isRequired,
  answer: PropTypes.object,
  allQuestions: PropTypes.array,
  parentQuestionId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

AnswerContentEditor.defaultProps = {
  answer: null,
  allQuestions: [],
  parentQuestionId: null,
};

export default AnswerContentEditor;

